import { Link, Navigate } from "react-router-dom";

import { useMe } from "../auth/use-me.js";
import { isTenantHost } from "../lib/tenant-host.js";

/**
 * Page d'accueil du HUB (`app.crayea.fr`), vue par un visiteur NON connecté :
 * deux portes, se connecter ou créer un compte. Un visiteur connecté ne la voit
 * jamais (redirection /dashboard), et sur un sous-domaine tenant elle n'existe
 * pas non plus.
 *
 * Elle portait jusqu'au 2026-07-25 un bandeau « Lot A.6 — squelette technique,
 * l'application sera implémentée à partir du Lot B » : vrai en avril, faux
 * depuis, et affiché à tout prospect qui ouvrait l'URL. Retiré, ainsi que le
 * lien « Tester l'API » (/ping), qui est un outil de dev.
 */

/**
 * Détecte si la page est servie depuis un sous-domaine tenant en dev ou
 * en prod (`<slug>.crayea.fr`, `<slug>.localhost`, etc.). Sur tenant, la
 * racine `/` n'a pas de page publique de sens — on redirige toujours vers
 * /dashboard, qui sera lui-même protégé par RequireAuth.
 */
function isOnTenantSubdomain(): boolean {
  if (typeof window === "undefined") return false;
  return isTenantHost(window.location.hostname);
}

export function HomePage() {
  // Sur sous-domaine tenant, la racine n'a pas de sens en mode "vitrine" :
  // on bascule sans hésiter vers le dashboard (RequireAuth gérera la 401
  // si la session ERP du sous-domaine n'a pas encore été posée).
  if (isOnTenantSubdomain()) {
    return <Navigate to="/dashboard" replace />;
  }

  // Sur le hub (localhost / app.crayea.fr), on regarde si l'utilisateur est
  // déjà authentifié. Si oui : il a très probablement atterri sur `/` par
  // habitude (clic sur logo, bookmark…) — on l'envoie sur /dashboard, qui
  // décidera lui-même via /choisir-domaine ou handoff direct selon ses
  // memberships. Si non : on affiche la page publique de transition.
  const { data, isLoading } = useMe();
  if (isLoading) {
    return <div className="dashboard-bg h-screen w-screen" aria-hidden="true" />;
  }
  if (data) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-8 px-6 py-12 text-center">
      <h1 className="text-5xl font-bold tracking-tight text-champagne-700">Crayéa</h1>

      <p className="text-lg text-gray-700">
        L&apos;ERP de la Champagne — conçu par et pour la filière.
      </p>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <Link
          to="/login"
          className="rounded-md bg-champagne-600 px-6 py-3 font-medium text-white shadow transition hover:bg-champagne-700 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-champagne-300"
        >
          Se connecter →
        </Link>
        <Link
          to="/signup"
          className="rounded-md border border-champagne-300 bg-white px-6 py-3 font-medium text-champagne-800 transition hover:border-champagne-500 hover:text-champagne-900"
        >
          Créer un compte
        </Link>
      </div>
    </main>
  );
}
